<template>
  <v-layout class="d-flex flex-column h-screen">
    <v-main :class="containerState" class="d-flex flex-grow-1">
      <v-container
        ref="mainContainerSummary"
        id="main-container-summary"
        :fluid="true"
      >
        <v-card class="mx-auto d-flex flex-column h-100" flat>
          <v-card-text v-if="overlay" class="pa-0">
            <v-overlay
              v-model="overlay"
              class="align-center justify-center"
              contained
              opacity="1"
              scrim="white"
            >
              <v-progress-circular
                indeterminate
                size="64"
                color="secondary"
              ></v-progress-circular>
            </v-overlay>
          </v-card-text>
          <v-card-text
            :opacity="opacityContent"
            class="py-0 overflow-y-auto overflow-x-hidden"
            :max-height="`calc(${this.containerHeight} - 52px)`"
          >
            <v-row>
              <v-col cols="12" class="mx-1 flex-grow-0 flex-shrink-1">
                <SummaryLayout
                  :apiUrl="kpimEditorConfig.api"
                  :realm="realm"
                  :kpi="kpi"
                  :validFormula="validFormula"
                  @writeCronJobStatus="writeCronJobStatus"
                  @toggleOverlay="toggleOverlay"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions
            class="elevation-2 pb-0 position-sticky bottom-0 bg-white z-index-1"
            :class="!overlay ? 'd-flex' : 'd-none'"
          >
            <v-btn color="secondary" variant="outlined" @click="abortKpi()">
              {{ $t("labels.cancel") }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="secondary"
              @click="routeTo('/editor/dashboard')"
              variant="elevated"
            >
              {{ $t("labels.back") }}
            </v-btn>
            <v-btn
              color="secondary"
              :disabled="
                (!kpiIsManual && !validFormula) ||
                (kpi.dependencyStatus !== 'active' && kpiId)
              "
              @click="saveAndContinue()"
              variant="elevated"
            >
              <span v-if="kpiId">{{ $t("labels.update") }}</span>
              <span v-else>{{ $t("labels.save") }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-container>
    </v-main>
  </v-layout>
</template>

<script>
import axios from "axios";
import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";
import version from "../version";
import SummaryLayout from "@/components/SummaryLayout.vue";
export default {
  name: "KpiEditorSummary",
  components: { SummaryLayout },
  mixins: [events, utils],
  props: ["kpimEditorConfig", "realm"],
  data: function () {
    return {
      appVersion: version,
      containerState: "expanded",
      loggedUserId: "",
      kpi: {},
      containerHeight: "0px",
      apiUrl: this.kpimEditorConfig.api,
      validFormula: false,
      overlay: true,
      opacityContent: 0,
    };
  },
  created() {
    this.overlay = true;
  },
  beforeMount() {
    this.kpi = JSON.parse(sessionStorage.getItem("kpi"));
  },

  mounted() {
    console.log("Mounted demf-kpi-editor-summary v", this.appVersion);
    this.loggedUserId = localStorage.getItem("loggedUserId");

    document.addEventListener(
      "language-localstorage-changed",
      this.handleLanguageChange
    );
    this.containerState =
      localStorage.getItem("sidenav") === "opened" ? "reduced" : "expanded";

    document.addEventListener(
      "sidenav-localstorage-changed",
      this.handleSidenavChange
    );
    this.updateContainerHeight(); // Update dimensions
    window.addEventListener("resize", this.updateContainerHeight);
    if (!this.kpiIsManual) {
      this.isFormulaValid();
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateContainerHeight);
    document.removeEventListener(
      "language-localstorage-changed",
      this.handleLanguageChange
    );
    document.removeEventListener(
      "sidenav-localstorage-changed",
      this.handleSidenavChange
    );
  },
  computed: {
    kpiId() {
      return this.getKpiId();
    },
    kpiIsManual() {
      return String(this.kpi?.kpiType || "").toLowerCase() === "manual";
    },
  },

  methods: {
    toggleOverlay(value) {
      this.opacityContent = value ? 0 : 1;
      this.overlay = value;
    },
    handleLanguageChange(event) {
      this.setLocale(event.detail.storage);
    },
    handleSidenavChange(event) {
      this.containerState =
        event.detail.storage === "opened" ? "reduced" : "expanded";
    },

    setEditableStep(step, value) {
      document.dispatchEvent(
        new CustomEvent("editable-steps", {
          detail: { step, value },
        })
      );
    },
    //Goes to a new path
    routeTo(newPath) {
      this.$router.push({
        path: newPath,
      });
    },
    //Abort the rule creation
    abortKpi() {
      sessionStorage.removeItem("kpi");
      this.routeTo(`/kpis`);
    },

    //Saves the updated Rule to sessionStorage and calls page routing
    saveAndContinue() {
      this.setEditableStep("summary", 1);
      this.kpiId ? this.updateKPI(this.kpi) : this.createKPI(this.kpi);
    },

    getKpiId() {
      return this.kpi?.id ? true : false;
    },

    createKPI(kpi) {
      this.overlay = true;
      axios
        .post(
          `${this.apiUrl}/${this.realm}/kpi`,
          {
            ...kpi,
          },
          {
            withCredentials: true,
          }
        )
        .then(() => {
          this.abortKpi();
        })
        .catch((err) => {
          this.setError(err, false);
          this.overlay = false;
        });
    },

    async updateKPI(kpi) {
      this.overlay = true;
      try {
        // Update KPI
        await axios.put(`${this.apiUrl}/${this.realm}/kpi/${kpi.id}`, kpi, {
          withCredentials: true,
        });

        // Check if cronjob status needs updating (only for non-manual KPIs)
        if (!this.kpiIsManual) {
          // Retrieve the saved KPI from session storage
          const savedKpiJson = sessionStorage.getItem("kpi");
          const savedKpi = savedKpiJson ? JSON.parse(savedKpiJson) : {};
          const savedStatus = savedKpi.formula?.job?.status;
          const actualStatus = kpi.formula?.job?.status;

          if (savedStatus !== actualStatus) {
            const suspend = actualStatus === "RUNNING" ? false : true;
            const cronJobId = kpi.formula.job.cronJobId;
            const cronJobUrl = `${this.apiUrl}/${this.realm}/cronjob/${cronJobId}?suspend=${suspend}`;

            const response = await axios.patch(
              cronJobUrl,
              { status: actualStatus },
              { withCredentials: true }
            );

            if (response) {
              sessionStorage.setItem("kpi", JSON.stringify(kpi));
            }
          }
        }

        this.abortKpi();
      } catch (err) {
        this.setError(err, false);
      } finally {
        this.overlay = false;
      }
    },

    writeCronJobStatus(status) {
      if (!this.kpiIsManual && this.kpi.formula?.job) {
        this.kpi.formula.job.status = status;
      }
    },

    isFormulaValid() {
      this.validFormula = false;
      const selected = Array.isArray(this.kpi?.definition?.selected)
        ? this.kpi.definition.selected
        : [];
      const details = Array.isArray(this.kpi?.formula?.details)
        ? this.kpi.formula.details
        : [];

      this.validFormula = selected.some((item) =>
        details.some(
          (detail) => item?.name && detail?.name && item.name === detail.name
        )
      );
    },

    //Calculate tag container height
    updateContainerHeight() {
      const element = document.getElementById("main-container-summary");
      if (element) {
        const elementCss = window.getComputedStyle(element);
        const paddingTop = parseFloat(
          elementCss.getPropertyValue("padding-top")
        );
        const paddingBottom = parseFloat(
          elementCss.getPropertyValue("padding-bottom")
        );
        this.containerHeight =
          element.offsetHeight - paddingTop - paddingBottom + "px";
      } else {
        console.warn("Not Found: ", element);
      }
    },
  },
};
</script>
<style scoped>
main.expanded {
  margin-left: 56px;
}
main.reduced {
  margin-left: 256px;
}
main #main-container-summary {
  /*ControlPanel total height - toolbar - stepper - Footer*/
  height: calc(100vh - 64px - 104px - 40px);
  overflow-y: auto;
}
@media only screen and (max-height: 768px) {
  main #main-container-summary {
    height: calc(100vh - 64px - 104px - 40px);
  }
}
</style>
